import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FadeInSection } from '@/components/ui/FadeInSection';
import { ArrowRight } from 'lucide-react';
import {
  DataScienceIcon,
  DossiersTechniquesIcon,
  InfographiesIcon,
  FormationsIcon,
  AuditExpertiseIcon,
  InterventionsIcon,
} from '@/components/prestations/ServiceIcons';

const getServices = (t: (key: string) => string) => [
  {
    titleKey: 'services.dataScience.title',
    descriptionKey: 'services.dataScience.description',
    icon: DataScienceIcon,
    color: '#55BC8C',
    link: '/prestations/data-science',
  },
  {
    titleKey: 'services.dossiersTechniques.title',
    descriptionKey: 'services.dossiersTechniques.description',
    icon: DossiersTechniquesIcon,
    color: '#9B59B6',
    link: '/prestations/dossiers-techniques',
    badge: 'popular',
  },
  {
    titleKey: 'services.infographies.title',
    descriptionKey: 'services.infographies.description',
    icon: InfographiesIcon,
    color: '#E96656',
    link: '/prestations/infographies',
  },
  {
    titleKey: 'services.formations.title',
    descriptionKey: 'services.formations.description',
    icon: FormationsIcon,
    color: '#0D974E',
    link: '/formations',
  },
  {
    titleKey: 'services.auditExpertise.title',
    descriptionKey: 'services.auditExpertise.description',
    icon: AuditExpertiseIcon,
    color: '#F9A826',
    link: '/prestations/audit-expertise',
  },
  {
    titleKey: 'services.interventions.title',
    descriptionKey: 'services.interventions.description',
    icon: InterventionsIcon,
    color: '#8B6F47',
    link: '/prestations/interventions',
  },
];

export function ServicesSection() {
  const { t, i18n } = useTranslation('home');
  const { locale } = useParams<{ locale: string }>();
  const currentLocale = locale || i18n.language || 'fr';
  const services = getServices(t);

  return (
    <section id="services-section" className="py-12 bg-white">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Section Header */}
        <FadeInSection>
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-aspexit-black">
              {t('services.title')}
            </h2>
          </div>
        </FadeInSection>

        {/* Services Grid */}
        <FadeInSection delay={0.2}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const IconComponent = service.icon;
            const linkTo = `/${currentLocale}${service.link}`;
            return (
              <div key={index} className="group h-full">
                <div className="relative h-full bg-white rounded-2xl border-2 border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-2xl hover:border-gray-200">
                  {/* Badge optionnel */}
                  {service.badge && (
                    <div className="absolute top-4 right-4 z-10">
                      <span className="px-3 py-1 bg-aspexit-green text-white text-xs font-semibold rounded-full">
                        {t(`services.${service.badge}`)}
                      </span>
                    </div>
                  )}

                  <div className="relative h-full flex flex-col p-8">
                    {/* Icon with circular colored background */}
                    <div className="flex items-center justify-center mb-6">
                      <div
                        className="w-32 h-32 rounded-full flex items-center justify-center transition-transform duration-300 group-hover:scale-105"
                        style={{
                          background: `linear-gradient(135deg, ${service.color}15, ${service.color}30)`,
                        }}
                      >
                        <IconComponent className="w-20 h-20" color={service.color} />
                      </div>
                    </div>

                    {/* Title */}
                    <h3 className="text-2xl font-heading font-bold text-aspexit-black mb-3 leading-tight">
                      {t(service.titleKey)}
                    </h3>

                    {/* Description */}
                    <p className="text-aspexit-gray text-sm leading-relaxed mb-6 flex-1">
                      {t(service.descriptionKey)}
                    </p>

                    {/* Bouton Voir Plus */}
                    <Link
                      to={linkTo}
                      className="inline-flex items-center gap-2 font-semibold text-sm transition-all duration-300 hover:gap-3"
                      style={{ color: service.color }}
                    >
                      <span>{t('services.viewMore')}</span>
                      <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
          </div>
        </FadeInSection>

        {/* View All Button */}
        <div className="text-center mt-12">
          <Button
            asChild
            variant="outline"
            size="lg"
            className="border-aspexit-black hover:bg-aspexit-green hover:text-white"
          >
            <Link to={`/${currentLocale}/prestations`}>{t('services.viewAll')}</Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
